// janus-engine/actions/status-provenance.ts — the browser janus lane's STATUS PROVENANCE assertion (owner 2026-10-08: "browser-side check
// only" — no PHP gameplay rule). Before a browser-resolved cast submits its `statusesAdded`, every row must be traceable to a source the
// cast legitimately reaches; ability_truth_commit_inc.php only checks that each status item is status-capable.
//
// A row's DISPATCH is the ability whose janus() frame applied it: `status_origin_id`, or — when that is null — the status item itself
// (apply_status_effect writes a null origin exactly when the applied item IS the dispatch's own ability). A row is admitted when:
//   1. it is the dispatch's OWN lingering status (status_item_id === dispatch: an ability-as-status, a persistent-area anchor / occupant
//      row, a reaction or trigger carrier applying itself — Fortunate, Prone, …);
// and, for rules 1 and 2, the dispatch is the casting ability, a volley's child, or a NESTED dispatch this use actually ran — one the
// verdict's own log rows name (roll_item_id): a trigger carrier, a reaction, a collision's Take Collision Damage, …;
//   2. its item is listed on the dispatch definition's applier lists (every status_effect_applied_* / passive_ability_applied_* layer) — for
//      the CASTING ability narrowed by status_effect_applied_is_caster_choice to the caster's pick (resolveCasterStatusChoice: the same
//      narrowing janus() applies), for any other dispatch narrowed with no pick (its frame passes no variable);
//   3. it is a known ENGINE status (a secondary source the pipeline writes itself): Concentrating on a dispatch that concentrates, the timed
//      cast's Casting / Searching, the PC dying cascade (including origin-less ladder rows on a PC held at 0 vitality),
//      Stunned after this use removes Hasted from the same target,
//      Maimed on a house-rule crit, Prone from a fall, the counterspell school / Spell Locked lockouts, the combat lockout row on an
//      ability that carries immune_to_ability_after_effect_until_combat_end;
//   4. its dispatch carries a random-table / contingent payload (the outcome is a table's attachment, chosen by the dice).
// Anything else returns a named gap before the browser submits its verdict.
import { resolveCasterStatusChoice, choiceEntryUuid } from '../ability/statuses/caster-status-choice.js';
import { ABILITY_LOCKOUT_ID, COMBAT_LOCKOUT_LAYER } from '../ability/statuses/combat-lockout.js';
import { DISMISS_SUMMON, PARALYZED } from '../ability/creature-control-ids.js';
import { creatureItemType, ecLean } from '../ability/context.js';
import { DEATH_STATE_SOURCE_IDS, HASTED_STATUS_ID, STUNNED_STATUS_ID } from '../death/state-ids.js';
import { SURFACE_LOOK_STATUS_IDS } from '../../shared/surface-geometry.js';

type Row = Record<string, unknown>;
const record = (v: unknown): Row | null => (v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null);
const upper = (v: unknown): string => String((Array.isArray(v) ? v[0] : v) ?? '').trim().toUpperCase();

export interface StatusProvenanceReaders {
  definition: (id: string) => Row | null;
  entries: (row: Row | null, key: string) => unknown[];
  has: (row: Row | null, key: string) => boolean;
  value: (row: Row | null, key: string) => unknown;
}
export interface StatusProvenanceRow { status_item_id: unknown; status_origin_id?: unknown; target?: unknown }
export interface StatusProvenanceLogRow { roll_item_id?: unknown; roll_type_1?: unknown }
export interface StatusProvenanceInput {
  /** The casting ability (a volley's parent). */
  abilityId: string;
  /** use_ability_variable_1 exactly as janus() received it for the casting ability (its caster-choice pick, when it carries one). */
  variable1: unknown;
  /** An instant volley's child: dispatched once per projectile with no variables. */
  childId?: string | null;
  statuses: readonly StatusProvenanceRow[];
  /** The verdict's log rows — the evidence that a nested dispatch ran in this use. */
  rows: readonly StatusProvenanceLogRow[];
  /** This verdict's working members, after damage (not the page's pre-cast cache). */
  members?: ReadonlyMap<string, Row>;
  statusesRemoved?: readonly StatusProvenanceRow[];
}

export const CONCENTRATING_ID = 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B';
/** Engine-written statuses (rule 3) — each the pipeline applies itself, never from an authored list. */
const ENGINE_STATUS_IDS: ReadonlySet<string> = new Set([
  '5D939B6E-B9BC-7AC5-5143-4F00F4189EA3', // Casting (timed-cast.ts)
  '5833A733-D277-0A60-3A94-A0F355A79A80', // Casting Ritual Spell (timed-cast.ts)
  'AE967694-B94B-F0FF-B680-829FDE6E8D58', // Searching (timed-cast.ts)
  '99C862B9-D6AE-4E18-7803-A6F9B42A88E1', // Crippled — apply_player_dying cascade
  '8AE129AF-52D8-CCE8-706A-85767DB52740', // Dying — apply_player_dying cascade
  'A5CDC77C-D224-DD5F-291B-EF8DF2EA443D', // Maimed — the dying cascade and the house-rule crit (damage-type-roll.ts)
  'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA', // Prone — a fall (fall-damage.ts)
  '62E83DEB-4569-AE3A-F671-FF514C508FD2', // Rotting — the actual death composer's self-cast corpse state
  'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8', // Fortunate — a luck gain (reaction-inventory.ts)
  '9DF8FF4C-33E8-8095-4876-ED3DBCD50B79', // Spell Locked — counterspell tier 3 (counterspell.ts)
  'EEDD357F-C1CA-44C1-A53F-DD06EEFBA597', // Holding Mastery — the Mastery marker touch on any Mastery change (ability/charges/mastery-marker.ts)
  // counterspell_lock_status_for_school() — the circle / sphere lockouts (reactions/counterspell-rules.ts SCHOOL_LOCK values)
  '2A64D1E8-7C3B-4F92-B5A8-E19D64C72F35', '9E15B7A2-3D48-4C61-A927-F84B21D6E593', '6C82F394-1A5D-4E76-8B34-D29E75A1C648',
  '4F37A8D1-9B62-4A15-C783-28E91B5F4D76', '8D21C6F5-4E93-4B27-A164-73F8D2E9B5A4', '3B96E4A7-2F51-4D38-9C82-46A1E7D3F925',
  '7A45D2B9-6C18-4F63-B591-82D4F6A3E1C7', '5E63B1C4-8A27-4D95-A348-91C6E2F7D8B3', '1D87F5A3-6B42-4C19-8E75-34A9D1B6C2F8',
  '9C34A6E1-5D78-4B26-A493-67F2E8D5B1A9', '6F92C3D7-1E85-4A64-B237-58D1A9F4E6C3',
]);
const DEATH_LADDER_IDS: ReadonlySet<string> = new Set(DEATH_STATE_SOURCE_IDS);
/** A dispatch whose outcome is a table attachment (random-table-payload.ts) — rule 4. */
const TABLE_PAYLOAD_LAYERS = ['roll_random_table', 'contingent_ability'];
const APPLIER_LAYER = /^(?:status_effect|passive_ability)_applied/;
/** The applier families the pipeline applies from (read through layer_entries(), so a provider-inherited list counts too). */
const APPLIER_FAMILIES = ['status_effect_applied_to_target_after_contesting_roll_failed', 'status_effect_applied_to_target_after_cast',
  'status_effect_applied_to_target_on_hit', 'status_effect_applied_to_caster_after_contesting_roll_failed', 'status_effect_applied_to_caster_after_cast',
  'status_effect_applied_to_caster_on_hit', 'status_effect_applied_to_caster_when_contestee_wins', 'passive_ability_applied_to_caster_after_cast'];

/** Every status the definition's applier layers list (rule 2): the named families plus any other applier key the definition carries. */
export function applierStatusIds(definition: Row, read: Pick<StatusProvenanceReaders, 'entries'>): Set<string> {
  const ids = new Set<string>();
  const layers = new Set([...APPLIER_FAMILIES, ...Object.keys(record(definition.item_layer_types) ?? {}), ...Object.keys(definition)].filter(key => APPLIER_LAYER.test(key)));
  for (const layer of layers) for (const entry of read.entries(definition, layer)) {
    const id = choiceEntryUuid(entry);
    if (id) ids.add(id);
  }
  return ids;
}

/** null when every row is admitted; else the first unadmitted row's reason (`status-provenance:<rule>:<status>:<dispatch>`). */
export function statusProvenanceGap(input: StatusProvenanceInput, read: StatusProvenanceReaders): string | null {
  const rootId = upper(input.abilityId);
  const childId = upper(input.childId);
  const appliers = new Map<string, Set<string> | null>();
  // A status APPLICATION row names the applied item, not a dispatch — it is never evidence that the item's own frame ran (Codex review).
  const logged = new Set(input.rows.filter(row => !String(row.roll_type_1 ?? '').startsWith('status_effect_applied'))
    .map(row => upper(row.roll_item_id)).filter(Boolean));
  const ran = (dispatch: string): boolean => dispatch === rootId || (childId !== '' && dispatch === childId) || logged.has(dispatch);
  const definitionOf = (id: string): Row | null => read.definition(id) ?? read.definition(id.toLowerCase());
  const appliersOf = (dispatch: string): Set<string> | null => {
    if (appliers.has(dispatch)) return appliers.get(dispatch)!;
    let definition = definitionOf(dispatch);
    if (definition) {
      // janus() narrows each dispatch's multi-entry applier lists to the caster's pick before anything is applied (R-151).
      const choice = resolveCasterStatusChoice({ read: read as never }, definition, dispatch === rootId ? input.variable1 : null);
      if (choice) definition = choice.ability;
    }
    const ids = definition ? applierStatusIds(definition, read) : null;
    appliers.set(dispatch, ids);
    return ids;
  };
  for (const status of input.statuses) {
    const id = upper(status.status_item_id);
    if (!id) return 'status-provenance:no-item';
    const dispatch = upper(status.status_origin_id) || id;
    if (rootId === DISMISS_SUMMON && dispatch === DISMISS_SUMMON && id === PARALYZED) continue;
    // The ladder writes no origin or dispatch log. Explicit appliers (Sleep / knockout)
    // still take rules 1/2; only a ladder row on this frame's zero-vitality PC is implicit.
    if (!upper(status.status_origin_id) && DEATH_LADDER_IDS.has(id)) {
      const target = input.members?.get(upper(status.target));
      const vitality = target ? ecLean(target, 'v_current') : null;
      if (target && creatureItemType(target) === 'player_character' && vitality != null && Number(vitality) === 0) continue;
    }
    if (id === STUNNED_STATUS_ID && input.statusesRemoved?.some(removed =>
      upper(removed.target) === upper(status.target) && upper(removed.status_item_id) === HASTED_STATUS_ID)) continue;
    const definition = definitionOf(dispatch);
    if (definition && id === SURFACE_LOOK_STATUS_IDS[String(read.value(definition, 'surface_type'))]
      && input.rows.some(row => row.roll_type_1 === 'surface_reacted' && upper(row.roll_item_id) === dispatch)) continue;
    // rule 1 — the dispatch's own row
    if (id === dispatch) {
      if (ran(dispatch) || ENGINE_STATUS_IDS.has(id)) continue;
      return `status-provenance:own-row-of-unlogged-dispatch:${id}`;
    }
    // rule 3 — engine statuses
    if (id === CONCENTRATING_ID) {
      if (definition && read.has(definition, 'spell_concentration')) continue;
      if (definition && dispatch === rootId && input.variable1 === 'spell_ritual' && read.has(definition, 'spell_ritual')
          && input.statuses.some(peer => upper(peer.status_item_id) === '5833A733-D277-0A60-3A94-A0F355A79A80'
            && upper(peer.target) === upper(status.target) && upper(peer.status_origin_id) === dispatch)) continue;
      return `status-provenance:concentration-without-concentration:${dispatch}`;
    }
    if (id === ABILITY_LOCKOUT_ID) {
      if (definition && read.has(definition, COMBAT_LOCKOUT_LAYER)) continue;
      return `status-provenance:lockout-without-layer:${dispatch}`;
    }
    if (ENGINE_STATUS_IDS.has(id)) continue;
    if (!definition) return `status-provenance:dispatch-definition-not-held:${id}:${dispatch}`;
    const role = dispatch === rootId ? 'cast' : childId !== '' && dispatch === childId ? 'volley-child' : 'nested';
    if (!ran(dispatch)) return `status-provenance:unlogged-dispatch:${id}:${dispatch}`;
    // rule 2 — the dispatch's (narrowed) applier lists
    if (appliersOf(dispatch)?.has(id)) continue;
    // rule 4 — a random-table / contingent outcome
    if (TABLE_PAYLOAD_LAYERS.some(layer => read.has(definition, layer))) continue;
    return `status-provenance:not-on-applier-lists:${role}:${id}:${dispatch}`;
  }
  return null;
}
